import { useCallback, useMemo, useState } from "react";

import { api } from "./client";
import { useResource } from "./useResource";

// Per-(user, kind) channel matrix (notifications.md §Channels). The server
// stores only rows the user has touched; an untouched kind falls back to these
// defaults, mirroring notify()'s coalesce (in-app on, email on, push off).
export type NotificationChannel = "email" | "in_app" | "web_push";

export type NotificationPreference = {
  readonly email: boolean;
  readonly in_app: boolean;
  readonly kind: string;
  readonly web_push: boolean;
};

export type ChannelPrefs = {
  readonly email: boolean;
  readonly in_app: boolean;
  readonly web_push: boolean;
};

const DEFAULTS: ChannelPrefs = { email: true, in_app: true, web_push: false };

export type UseNotificationPreferences = {
  readonly loading: boolean;
  readonly preferenceFor: (kind: string) => ChannelPrefs;
  readonly setPreference: (
    kind: string,
    channel: NotificationChannel,
    value: boolean,
  ) => void;
};

export const useNotificationPreferences = (): UseNotificationPreferences => {
  const { data, loading } = useResource<ReadonlyArray<NotificationPreference>>(
    "/notification-preferences",
  );
  // Local optimistic overlay on top of the fetched rows, so a toggle reflects
  // instantly and a failed PATCH keeps the chosen value (best-effort persist,
  // same as the global preferences provider).
  const [overrides, setOverrides] = useState<
    Record<string, Partial<ChannelPrefs>>
  >({});

  const serverByKind = useMemo(() => {
    const map: Record<string, ChannelPrefs> = {};
    for (const row of data ?? []) {
      map[row.kind] = {
        email: row.email,
        in_app: row.in_app,
        web_push: row.web_push,
      };
    }
    return map;
  }, [data]);

  const preferenceFor = useCallback(
    (kind: string): ChannelPrefs => ({
      ...DEFAULTS,
      ...serverByKind[kind],
      ...overrides[kind],
    }),
    [serverByKind, overrides],
  );

  const setPreference = useCallback(
    (kind: string, channel: NotificationChannel, value: boolean): void => {
      setOverrides((prev) => ({
        ...prev,
        [kind]: { ...prev[kind], [channel]: value },
      }));
      void api
        .patch("/notification-preferences", [{ [channel]: value, kind }])
        .catch(() => {
          // Best-effort: keep the local choice even if the write fails.
        });
    },
    [],
  );

  return { loading, preferenceFor, setPreference };
};
